import {
  isOrgBrandedSubdomainActive,
  orgPublicBaseUrl,
  orgPublicPath,
  type OrgPublicHostMode,
  type OrgPublicUrlOptions,
} from '@court-sync/shared';
import { getAcademyHostContext, type AcademyHostMode } from './academyHost';

export type OrgPathFields = {
  slug: string;
  subdomain?: string | null;
  subdomainEnabled?: boolean;
};

export type OrgBrandedUrlContext = {
  tier?: string;
  clubTier?: string | null;
};

export function isOrgBrandedPublicSiteActive(
  org: OrgPathFields,
  context?: OrgBrandedUrlContext
): boolean {
  return isOrgBrandedSubdomainActive(org, context?.tier ?? 'starter', context?.clubTier);
}

/** Absolute embed URL (`?embed=1`) — uses branded subdomain when that is the active public site. */
export function buildOrgEmbedAbsoluteUrl(
  org: OrgPathFields,
  subpath: string,
  context?: OrgBrandedUrlContext
): string {
  const brandedActive = isOrgBrandedPublicSiteActive(org, context);
  return `${orgPublicBaseUrl(org, subpath, undefined, 'path', brandedActive)}?embed=1`;
}

export function getOrgHostMode(hostname?: string): OrgPublicHostMode {
  return getAcademyHostContext(hostname).mode;
}

function resolveWebAppOriginForPathFallback(): string {
  if (typeof window === 'undefined') return 'https://app.courtsync.io';
  const { hostname, port } = window.location;
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    const portSuffix = port ? `:${port}` : '';
    return `http://localhost${portSuffix}`;
  }
  if (hostname.endsWith('.dev.courtsync.io') || hostname === 'dev.courtsync.io') {
    return 'https://app.dev.courtsync.io';
  }
  return 'https://app.courtsync.io';
}

/** Relative org-scoped path for the current host mode. */
export function buildOrgPath(
  org: OrgPathFields,
  subpath: string,
  hostMode: OrgPublicHostMode = getOrgHostMode()
): string {
  return orgPublicPath(org, subpath, hostMode);
}

/** Absolute public URL for an org path. */
export function buildOrgAbsoluteUrl(
  org: OrgPathFields,
  subpath = '',
  options?: OrgPublicUrlOptions,
  hostMode: OrgPublicHostMode = 'path'
): string {
  return orgPublicBaseUrl(org, subpath, options, hostMode);
}

/** Login path for the unified member portal. */
export function buildOrgLoginPath(
  org: OrgPathFields,
  returnPath?: string,
  hostMode: OrgPublicHostMode = getOrgHostMode()
): string {
  const loginPath = buildOrgPath(org, '/login', hostMode);
  if (!returnPath) return loginPath;
  const sep = loginPath.includes('?') ? '&' : '?';
  return `${loginPath}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/** Register path for the unified member portal. */
export function buildOrgRegisterPath(
  org: OrgPathFields,
  returnPath?: string,
  hostMode: OrgPublicHostMode = getOrgHostMode()
): string {
  const registerPath = buildOrgPath(org, '/register', hostMode);
  if (!returnPath) return registerPath;
  const sep = registerPath.includes('?') ? '&' : '?';
  return `${registerPath}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

export type OrgCreateAccountModules = {
  hasAcademy: boolean;
  hasClub: boolean;
  membershipOnlineJoinEnabled: boolean;
};

export function isClubMembershipReturnPath(returnPath: string): boolean {
  return returnPath.includes('/join') || returnPath.includes('/membership');
}

/**
 * Club-only orgs (and combined orgs when the login return path is club membership)
 * send new visitors to online join instead of the academy register page.
 *
 * @deprecated Prefer {@link OrgRegisterDialog} for in-context registration; kept for full-page fallbacks.
 */
export function resolveOrgCreateAccountPath(
  org: OrgPathFields,
  modules: OrgCreateAccountModules,
  returnPath?: string,
  hostMode: OrgPublicHostMode = getOrgHostMode()
): string {
  return buildOrgRegisterPath(org, returnPath, hostMode);
}

/** Email-verification path for org member signup (not platform /email-verification). */
export function buildOrgEmailVerificationPath(
  org: OrgPathFields,
  options: { email: string; returnUrl: string; academySlug?: string },
  hostMode: OrgPublicHostMode = getOrgHostMode()
): string {
  const path = buildOrgPath(org, '/email-verification', hostMode);
  const params = new URLSearchParams({
    email: options.email,
    returnUrl: options.returnUrl,
  });
  if (options.academySlug) {
    params.set('academySlug', options.academySlug);
  }
  return `${path}?${params.toString()}`;
}

/** Forgot-password path for the unified member portal. */
export function buildOrgForgotPasswordPath(
  org: OrgPathFields,
  returnPath?: string,
  hostMode: OrgPublicHostMode = getOrgHostMode()
): string {
  const path = buildOrgPath(org, '/forgot-password', hostMode);
  if (!returnPath) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

function appendReturnUrlToAbsolute(url: string, returnPath?: string): string {
  if (!returnPath) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/** Path-mode auth URL for embed link-out (always opens on app origin). */
export function buildOrgAuthLinkOutUrl(
  org: OrgPathFields,
  subpath: string,
  returnPath?: string
): string {
  const path = orgPublicPath({ slug: org.slug }, subpath, 'path');
  return appendReturnUrlToAbsolute(`${resolveWebAppOriginForPathFallback()}${path}`, returnPath);
}

/** Firebase password-reset continue URLs — always path-mode app origin. */
export function buildOrgPasswordResetContinueUrls(
  org: OrgPathFields,
  returnPath?: string
): { primary: string; fallback: string } {
  const pathReset = orgPublicPath({ slug: org.slug }, '/reset-password', 'path');
  const pathModeUrl = appendReturnUrlToAbsolute(
    `${resolveWebAppOriginForPathFallback()}${pathReset}`,
    returnPath
  );
  return { primary: pathModeUrl, fallback: pathModeUrl };
}

export type OrgHostMode = AcademyHostMode;
